import { useEffect, useState } from "react";
import { Accordion, Alert, Card, Col, Form, FormGroup, Image, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import UbicationTypeForm from "../../../components/forms/configuration/UbicationTypeForm";
import { Table } from "react-bootstrap";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import PaginationComponent from "../../../components/PaginationComponent";
import TableHeaderSortComponent from "../../../components/TableHeaderSortComponent";
import APP_CONF from "../../../scripts/constants";
import BodyService from "../../../services/body.service";
import UseAlertConfirm from "../../../hooks/UseAlertConfirm";
import { useForm } from "react-hook-form";
import UbicationTypeService from "../../../services/ubicationType.service";
import UserPermissionService from "../../../services/userPermission.service";

const UbicationType = () => {
    const { t } = useTranslation();

    const { showModal } = UseAlertConfirm();

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: APP_CONF.ITEMS_PER_PAGE,
        numPages: 1
    });

    const [sortColumn, setSortColumn] = useState({
        sortByColumn: null,
        orderBy: null
    });

    const [ubicationTypes, setUbicationTypes] = useState([]);

    const [translations, setTranslations] = useState([]);

    const [permissions, setPermissions] = useState({});

    const [success, setSuccess] = useState({
        create: false,
        delete: false,
    });

    const [errors, setErrors] = useState({
        create: false,
        delete: false,
    });

    const { register, handleSubmit } = useForm({
        mode: "all",
    });

    const [filter, setFilter] = useState({
        active: "",
        search: ""
    });

    const getUbicationTypes = async () => {
        try {
            const data = await UbicationTypeService.getAll(pagination, filter, sortColumn);
            setUbicationTypes(data.content);
            setPagination({
                ...pagination,
                totalItems: data.totalElements,
                numPages: data.totalPages
            });
        } catch (response) {
            if (response?.data?.errorCode) {
                switch (response.data.errorCode) {
                    case 13002:
                        setErrors({ ...errors, email: true });
                        break;
                    default:
                        setErrors({ ...errors, create: true });
                }
            }
        }
    };

    const filterSearch = (data) => {
        setFilter({
            active: data.active,
            search: data.search,
        });
    };

    const getEmptyTranslations = async () => {
        const data = await BodyService.getEmptyTranslations();
        setTranslations(data);
    };

    const getPermissions = async () => {
        try {
            const data = await UserPermissionService.getPermissionsByConnected();
            setPermissions(data);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        const loadData = async () => {
            await getPermissions();
            await getEmptyTranslations();
        }

        loadData();
    }, []);

    useEffect(() => {
        getUbicationTypes();
    }, [pagination.currentPage, sortColumn, filter])

    const createUbicationType = async (data) => {
        setErrors({ ...errors, create: false });
        setSuccess({ ...success, create: false });
        try {
            await UbicationTypeService.create(data);
            getEmptyTranslations();
            setSuccess({ ...success, create: true });
            getUbicationTypes();
        } catch (error) {
            console.error(error.response);
            setErrors({ ...errors, create: true });
        }
    };

    const deleteUbicationType = async (id) => {
        showModal({
            title: t("general.delete.title"),
            body: t("general.delete.message"),
            confirmButtonVariant: "danger",
            onConfirm: async () => {
                setErrors({ ...errors, create: false });
                setSuccess({ ...success, create: false });
                try {
                    await UbicationTypeService.remove(id);
                    setSuccess({ ...success, delete: true });
                    getUbicationTypes();
                } catch (e) {
                    console.error(e.response);
                    setErrors({ ...errors, delete: true });
                }
            }
        });
    };

    return (
        <>
            <div className={"d-sm-flex align-items-center justify-content-between mb-4"}>
                <h1 className={"h3 mb-0 text-gray-800"}>{t('ubicationType.title')}</h1>
            </div>
            <Alert variant={"danger"} show={errors.create} dismissible onClose={() => setErrors({ ...errors, create: false })}>
                {t('ubicationType.error.create')}
            </Alert>

            <Alert variant={"success"} show={success.create} dismissible onClose={() => setSuccess({ ...success, create: false })}>
                {t('ubicationType.success.create')}
            </Alert>

            <Alert variant={"success"} show={success.delete} dismissible onClose={() => setSuccess({ ...success, delete: false })}>
                {t('ubicationType.success.delete')}
            </Alert>

            <Alert variant={"danger"} show={errors.delete} dismissible onClose={() => setErrors({ ...errors, delete: false })}>
                {t('ubicationType.error.delete')}
            </Alert>

            {permissions.locationTypes?.read && permissions.locationTypes?.create ? 
            <Card className={"shadow mb-4"}>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><h6 className={"m-0 fw-bold text-primary"}>{t('ubicationType.form.title')}</h6>
                        </Accordion.Header>
                        <Accordion.Body>
                            <UbicationTypeForm onSubmit={createUbicationType} translations={translations} permissions={permissions}/>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card>            
            : null}

            <Card className={"shadow mb-4"}>
                <Card.Header className={"py-3"}>
                    <h6 className={"m-0 fw-bold text-primary"}>{t('ubicationType.table.title')}</h6>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(filterSearch)}>
                        <Row>
                            <Col lg="2" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('ubicationType.filter.active.title')}</Form.Label>
                                    <Form.Select {...register('active')} aria-label="Default select example">
                                        <option value="">{t('ubicationType.filter.active.value1')}</option>
                                        <option value={true}>{t('ubicationType.filter.active.value2')}</option>
                                        <option value={false}>{t('ubicationType.filter.active.value3')}</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col lg="3" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('ubicationType.filter.search')}</Form.Label>
                                    <Form.Control type="text" {...register("search")} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Button type="submit" className={"mt-3"} disabled={!permissions.locationTypes?.read}>{t('ubicationType.filter.button')}</Button>
                    </Form>
                    <hr />
                    <Table responsive>
                        <thead>
                            <tr>
                                <TableHeaderSortComponent scope={"col"} text={t('ubicationType.table.name')} column={"name"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <TableHeaderSortComponent scope={"col"} text={t('ubicationType.table.active')} column={"active"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                ubicationTypes ? ubicationTypes.map((ubicationType, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="d-flex flex-row flex-nowrap">
                                                {Object.values(ubicationType.ubicationType).map((translation, i) =>
                                                    <div key={i} className="me-2">
                                                        <Image src={translation.language.icon} className="me-2 flags-img-sm" />
                                                        {i + 1 === Object.values(ubicationType.ubicationType).length ? translation.value : translation.value + ' | '}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>{ubicationType?.active ? 'Sí' : 'No'}</td>
                                        <td className={"text-center"}>
                                            <Button variant="link" size="sm" className="ms-2" to={`/configuration/ubication-type/${ubicationType.id}`} as={Link} disabled={!permissions.locationTypes?.read}>
                                                <FontAwesomeIcon icon="edit" />
                                            </Button>
                                            <Button variant="link" size="sm" className="text-danger" onClick={() => deleteUbicationType(ubicationType.id)} disabled={!permissions.locationTypes?.delete}>
                                                <FontAwesomeIcon icon="trash" />
                                            </Button>
                                        </td>
                                    </tr>
                                )) :
                                    <tr>
                                        <td colSpan={4}>{t('general.empty')}</td>
                                    </tr>
                            }
                        </tbody>
                    </Table>
                    <PaginationComponent pagination={pagination}
                        setPagination={setPagination}
                        alwaysShown={ubicationTypes.length > 0} />
                </Card.Body>
            </Card>
            <hr />
        </>
    );
};

export default UbicationType; 